import { postProductEvents, ProductEventTransportError } from "../api/productEvents";
import type { ProductEventEnvelope } from "../shared/interfaces";

const MAX_QUEUE_SIZE_PER_ORG = 500;
const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5_000;
// The transport (p-retry in postProductEvents) owns the only true retry layer;
// this is the budget on how many times a failed batch may re-enter the queue
// before its events are dropped.
const MAX_DELIVERY_ATTEMPTS = 3;
// Browsers cap concurrently in-flight keepalive request bodies at ~64 KiB;
// stay conservatively below so unload batches are not rejected outright.
const KEEPALIVE_BODY_BUDGET_BYTES = 56 * 1024;

type ProductEventTransport = (
    batch: { org_slug: string; events: ProductEventEnvelope[] },
    keepalive?: boolean,
) => Promise<void>;

interface OrganizationQueue {
    entries: ProductEventEnvelope[];
    flushTimer: ReturnType<typeof setTimeout> | null;
    inFlight: Promise<boolean> | null;
    inFlightEntries: ProductEventEnvelope[] | null;
    deliveryAttempts: Map<string, number>;
    drainGeneration: number;
}

function createOrganizationQueue(): OrganizationQueue {
    return {
        entries: [],
        flushTimer: null,
        inFlight: null,
        inFlightEntries: null,
        deliveryAttempts: new Map(),
        drainGeneration: 0,
    };
}

/**
 * Tenant-partitioned, asynchronous first-party event delivery.
 *
 * The queue only *starts* batches for the currently active organization. A
 * bounded drain (see `drainOrganization`) can time out while `postProductEvents`
 * is still in flight or waiting to retry, so an old-organization HTTP attempt can
 * continue after `activeOrganization` changes. The server independently verifies
 * the stamped organization against the authenticated session, so those races
 * cannot turn an old-project event into a new-project event — that check, not
 * the queue, is the actual race-safety guarantee.
 */
export class ProductEventQueue {
    private readonly queues = new Map<string, OrganizationQueue>();
    private activeOrganization: string | null = null;

    constructor(private readonly transport: ProductEventTransport = postProductEvents) {}

    setActiveOrganization(orgSlug: string | null): void {
        const normalized = orgSlug?.trim() || null;
        if (normalized === this.activeOrganization) return;

        const previous = this.activeOrganization;
        this.activeOrganization = normalized;
        if (previous) this.clearFlushTimer(this.queues.get(previous));
        if (normalized) {
            const state = this.queues.get(normalized);
            if (state?.entries.length) this.scheduleFlush(normalized, state);
        }
    }

    async enqueue(event: ProductEventEnvelope): Promise<void> {
        const orgSlug = event.org_slug.trim();
        if (!orgSlug || orgSlug !== this.activeOrganization) {
            throw new Error("Refusing to queue a product event outside the active organization");
        }

        const state = this.stateFor(orgSlug);
        state.entries.push(event);
        this.enforceQueueCap(state);
        if (state.entries.length >= BATCH_SIZE) {
            // A flush already in flight will pick these entries up via its own
            // reschedule once it settles (see flush()'s finally block below).
            // Calling flush() again here wouldn't start a new request — it would
            // just chain another dangling promise onto the same in-flight batch,
            // and a stalled request plus sustained events would accumulate those
            // unboundedly.
            if (!state.inFlight) void this.flush(false, orgSlug);
        } else {
            this.scheduleFlush(orgSlug, state);
        }
    }

    async flush(keepalive = false, orgSlug = this.activeOrganization): Promise<boolean> {
        if (!orgSlug || orgSlug !== this.activeOrganization) return false;
        const state = this.queues.get(orgSlug);
        if (!state) return true;
        if (state.inFlight) {
            // Page-hide/visibility flushes must include slices emitted after a regular
            // batch started. A second keepalive request is safe because event IDs are
            // idempotent and every batch is independently tenant-validated.
            if (keepalive && state.entries.length > 0) {
                return this.flushBatch(orgSlug, state, true);
            }
            return state.inFlight;
        }
        if (state.entries.length === 0) return true;

        this.clearFlushTimer(state);
        const operation = this.flushBatch(orgSlug, state, keepalive);
        state.inFlight = operation;
        try {
            return await operation;
        } finally {
            if (state.inFlight === operation) state.inFlight = null;
            if (orgSlug === this.activeOrganization && state.entries.length > 0) {
                this.scheduleFlush(orgSlug, state);
            }
        }
    }

    /**
     * Synchronously drains `orgSlug`'s entire backlog into BATCH_SIZE-sized
     * keepalive requests, firing each one without waiting for it to settle.
     *
     * Used on the pagehide/unload path: nothing after the event handler
     * returns is guaranteed to run, so a single-batch flush() would strand
     * (and lose) any remainder past BATCH_SIZE — for example, events that
     * piled up behind an already in-flight request. Every batch is spliced
     * off state.entries before any transport call is awaited, so the drain
     * is atomic with respect to other synchronous callers (nothing else
     * runs between splices). Firing every batch without waiting on it is
     * safe for the same reason flush()'s in-flight keepalive branch is:
     * events dedupe server-side by ID, and every batch is independently
     * tenant-validated.
     */
    flushAll(keepalive: boolean, orgSlug = this.activeOrganization): void {
        if (!orgSlug || orgSlug !== this.activeOrganization) return;
        const state = this.queues.get(orgSlug);
        if (!state) return;
        this.clearFlushTimer(state);
        // Batches fired past the browser's shared keepalive body quota reject
        // immediately, and at pagehide their asynchronously requeued rows die
        // with the page — spend the budget on the oldest events instead of
        // firing everything and losing the excess.
        let budget = KEEPALIVE_BODY_BUDGET_BYTES;
        // A regular (non-keepalive) request may already hold one batch that the
        // browser can cancel at pagehide; resend it with keepalive — event IDs
        // dedupe server-side, so the duplicate is safe.
        const inFlightEntries = state.inFlightEntries;
        if (inFlightEntries?.length) {
            const body = { org_slug: orgSlug, events: [...inFlightEntries] };
            budget -= JSON.stringify(body).length;
            void this.transport(body, keepalive).catch((error) =>
                console.error("[Analytics] Failed to deliver product events:", error),
            );
        }
        while (state.entries.length > 0) {
            const nextBatch = state.entries.slice(0, BATCH_SIZE);
            const size = JSON.stringify({ org_slug: orgSlug, events: nextBatch }).length;
            if (size > budget) break;
            budget -= size;
            void this.flushBatch(orgSlug, state, keepalive);
        }
    }

    async drainOrganization(
        orgSlug: string,
        keepalive = false,
        timeoutMs?: number,
    ): Promise<boolean> {
        const drain = this.drainOrganizationUntilEmpty(orgSlug, keepalive);
        if (timeoutMs === undefined) return drain;
        if (timeoutMs < 0) throw new Error("timeoutMs cannot be negative");

        let timeout: ReturnType<typeof setTimeout> | undefined;
        const deadline = new Promise<boolean>((resolve) => {
            timeout = setTimeout(() => {
                const state = this.queues.get(orgSlug);
                if (state) {
                    state.drainGeneration += 1;
                    state.inFlight = null;
                }
                resolve(false);
            }, timeoutMs);
        });
        try {
            return await Promise.race([drain, deadline]);
        } finally {
            if (timeout) clearTimeout(timeout);
        }
    }

    private async drainOrganizationUntilEmpty(
        orgSlug: string,
        keepalive: boolean,
    ): Promise<boolean> {
        if (orgSlug !== this.activeOrganization) return false;
        const state = this.queues.get(orgSlug);
        const generation = state?.drainGeneration;
        while (state?.inFlight || state?.entries.length) {
            if (!(await this.flush(keepalive, orgSlug))) return false;
            if (orgSlug !== this.activeOrganization || state.drainGeneration !== generation) {
                return false;
            }
        }
        return true;
    }

    size(orgSlug = this.activeOrganization): number {
        if (orgSlug) return this.queues.get(orgSlug)?.entries.length ?? 0;
        return Array.from(this.queues.values()).reduce(
            (total, state) => total + state.entries.length,
            0,
        );
    }

    dispose(): void {
        this.queues.forEach((state) => this.clearFlushTimer(state));
        this.queues.clear();
        this.activeOrganization = null;
    }

    private stateFor(orgSlug: string): OrganizationQueue {
        const existing = this.queues.get(orgSlug);
        if (existing) return existing;
        const created = createOrganizationQueue();
        this.queues.set(orgSlug, created);
        return created;
    }

    private async flushBatch(
        orgSlug: string,
        state: OrganizationQueue,
        keepalive: boolean,
    ): Promise<boolean> {
        if (orgSlug !== this.activeOrganization || state.entries.length === 0) return false;
        const entries = state.entries.splice(0, BATCH_SIZE);
        state.inFlightEntries = entries;
        try {
            await this.transport({ org_slug: orgSlug, events: entries }, keepalive);
            entries.forEach((entry) => state.deliveryAttempts.delete(entry.event_id));
            return true;
        } catch (error) {
            if (error instanceof ProductEventTransportError && !error.retryable) {
                // A permanent rejection (4xx) would poison the queue head if
                // requeued: every later flush resends and re-fails the same
                // batch. Dead-letter it to the console instead.
                console.error("[Analytics] Dropping batch permanently rejected by the server:", error);
                entries.forEach((entry) => state.deliveryAttempts.delete(entry.event_id));
                return false;
            }
            console.error("[Analytics] Failed to deliver product events:", error);
            // The transport's own retry policy (see postProductEvents) already
            // exhausted its budget, so requeueing is bounded: each event gets
            // MAX_DELIVERY_ATTEMPTS trips through the queue before it is
            // dropped. Re-sending a batch the server actually received is
            // safe, since events dedupe server-side by event ID.
            const survivors = entries.filter((entry) => {
                const attempts = (state.deliveryAttempts.get(entry.event_id) ?? 0) + 1;
                if (attempts >= MAX_DELIVERY_ATTEMPTS) {
                    state.deliveryAttempts.delete(entry.event_id);
                    return false;
                }
                state.deliveryAttempts.set(entry.event_id, attempts);
                return true;
            });
            if (survivors.length < entries.length) {
                console.error(
                    `[Analytics] Dropping ${entries.length - survivors.length} event(s) after ${MAX_DELIVERY_ATTEMPTS} delivery attempts`,
                );
            }
            state.entries.unshift(...survivors);
            this.enforceQueueCap(state);
            return false;
        } finally {
            if (state.inFlightEntries === entries) state.inFlightEntries = null;
        }
    }

    private enforceQueueCap(state: OrganizationQueue): void {
        const overflow = state.entries.length - MAX_QUEUE_SIZE_PER_ORG;
        if (overflow > 0) {
            for (const evicted of state.entries.splice(0, overflow)) {
                state.deliveryAttempts.delete(evicted.event_id);
            }
        }
    }

    private scheduleFlush(orgSlug: string, state: OrganizationQueue): void {
        if (state.flushTimer || orgSlug !== this.activeOrganization) return;
        state.flushTimer = setTimeout(() => {
            state.flushTimer = null;
            void this.flush(false, orgSlug);
        }, FLUSH_INTERVAL_MS);
    }

    private clearFlushTimer(state: OrganizationQueue | undefined): void {
        if (!state) return;
        if (state.flushTimer) clearTimeout(state.flushTimer);
        state.flushTimer = null;
    }
}

export const productEventQueue = new ProductEventQueue();
